import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Globe, TrendingUp, ExternalLink, Search, Share2, Hash, MousePointer } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const TrafficSources = ({ timeRange = 'today' }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalVisits, setTotalVisits] = useState(0);

  useEffect(() => {
    fetchTrafficSources();
    // Refresh every 10 seconds
    const interval = setInterval(fetchTrafficSources, 10000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const fetchTrafficSources = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/traffic/analytics?time_range=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.analytics) {
        const sourcesData = data.analytics.trafficSources || [];
        setSources(sourcesData);
        
        // Calculate total visits
        const total = sourcesData.reduce((sum, source) => sum + source.count, 0);
        setTotalVisits(total);
      }
    } catch (error) {
      console.error('Error fetching traffic sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (source) => {
    const lowerSource = source.toLowerCase();
    if (lowerSource.includes('google')) return <Search className="h-4 w-4 text-red-500" />;
    if (lowerSource.includes('facebook')) return <Share2 className="h-4 w-4 text-blue-600" />;
    if (lowerSource.includes('instagram')) return <Hash className="h-4 w-4 text-pink-500" />;
    if (lowerSource.includes('twitter') || lowerSource.includes('x.com')) return <Share2 className="h-4 w-4 text-sky-500" />;
    if (lowerSource.includes('linkedin')) return <Share2 className="h-4 w-4 text-blue-700" />;
    if (lowerSource === 'direct') return <MousePointer className="h-4 w-4 text-emerald-600" />;
    if (lowerSource === 'referral') return <ExternalLink className="h-4 w-4 text-purple-600" />;
    return <Globe className="h-4 w-4 text-gray-600" />;
  };

  const getSourceColor = (source) => {
    const lowerSource = source.toLowerCase();
    if (lowerSource.includes('google')) return 'bg-red-50 border-red-200';
    if (lowerSource.includes('facebook')) return 'bg-blue-50 border-blue-200';
    if (lowerSource.includes('instagram')) return 'bg-pink-50 border-pink-200';
    if (lowerSource.includes('twitter') || lowerSource.includes('x.com')) return 'bg-sky-50 border-sky-200';
    if (lowerSource.includes('linkedin')) return 'bg-blue-50 border-blue-300';
    if (lowerSource === 'direct') return 'bg-emerald-50 border-emerald-200';
    if (lowerSource === 'referral') return 'bg-purple-50 border-purple-200';
    return 'bg-gray-50 border-gray-200';
  };

  const getSourceBadgeColor = (source) => {
    const lowerSource = source.toLowerCase();
    if (lowerSource.includes('google')) return 'bg-red-600';
    if (lowerSource.includes('facebook')) return 'bg-blue-600';
    if (lowerSource.includes('instagram')) return 'bg-pink-600';
    if (lowerSource.includes('twitter') || lowerSource.includes('x.com')) return 'bg-sky-500';
    if (lowerSource.includes('linkedin')) return 'bg-blue-700';
    if (lowerSource === 'direct') return 'bg-emerald-600';
    if (lowerSource === 'referral') return 'bg-purple-600';
    return 'bg-gray-600';
  };

  const calculatePercentage = (count) => {
    if (totalVisits === 0) return 0;
    return ((count / totalVisits) * 100).toFixed(1);
  };

  return (
    <Card className="h-[500px] shadow-lg border-2 border-indigo-200">
      <CardContent className="p-3 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-indigo-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1">
              <TrendingUp className="h-4 w-4 text-indigo-600" />
              Traffic Sources
            </h3>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-indigo-600">{totalVisits}</p>
            <p className="text-xs text-gray-500">Visits</p>
          </div>
        </div>

        {/* Sources List - Scrollable */}
        <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : sources.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Globe className="h-8 w-8 mb-2 text-gray-400" />
              <p className="text-xs">No sources yet</p>
            </div>
          ) : (
            sources.map((source, index) => {
              const percentage = calculatePercentage(source.count);
              
              return (
                <div
                  key={index}
                  className={`p-2 rounded-lg border ${getSourceColor(source.source)} hover:shadow-md transition-all`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {getSourceIcon(source.source)}
                      <div>
                        <p className="font-bold text-gray-900 text-xs">{source.source}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`${getSourceBadgeColor(source.source)} text-white px-2 py-0.5 rounded-full text-xs font-bold`}>
                        {source.count}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-1">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`${getSourceBadgeColor(source.source)} h-1.5 rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <span className="text-gray-600">
                      {percentage}%
                    </span>
                    <span className="text-gray-500">
                      #{index + 1}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer Stats */}
        {!loading && sources.length > 0 && (
          <div className="mt-2 pt-2 border-t border-indigo-100">
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="bg-indigo-50 rounded p-1">
                <p className="text-xs text-indigo-700 font-semibold truncate">{sources[0]?.source}</p>
              </div>
              <div className="bg-indigo-50 rounded p-1">
                <p className="text-xs text-indigo-700 font-semibold">{sources.length} src</p>
              </div>
              <div className="bg-indigo-50 rounded p-1">
                <p className="text-xs text-indigo-700 font-semibold">
                  {calculatePercentage(sources[0]?.count || 0)}%
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Custom Scrollbar Styling */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #6366f1;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4f46e5;
        }
      `}</style>
    </Card>
  );
};

export default TrafficSources;
